import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { getCompensationPayoutReconDetails, type CompensationReconDetails } from '@/services/compensationPayoutService';

interface Props {
  reconId: string | null;
  initial?: CompensationReconDetails;
  source: object | null;
  children: (details: CompensationReconDetails | null) => ReactNode;
}

/** Mount only inside an opened breakdown or tooltip. */
export function PayoutAccountingDetails({ reconId, initial, source, children }: Props) {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{
    reconId: string; source: object | null; attempt: number;
    data?: CompensationReconDetails; failed?: boolean;
  }>();

  useEffect(() => {
    if (!reconId || initial) {
      return;
    }
    let active = true;
    void getCompensationPayoutReconDetails(reconId).then(data => {
      if (active) {
        setResult({ reconId, source, attempt, data });
      }
    }, () => {
      if (active) {
        setResult({ reconId, source, attempt, failed: true });
      }
    });
    return () => {
      active = false;
    };
  }, [reconId, initial, source, attempt]);

  if (initial || !reconId) {
    return children(initial ?? null);
  }
  const current = result?.reconId === reconId && result.source === source && result.attempt === attempt ? result : undefined;
  if (current?.data) {
    return children(current.data);
  }
  if (current?.failed) {
    return <div className="space-y-2 text-sm">
      <p role="alert">Could not load accounting details.</p>
      <Button variant="outline" onClick={() => setAttempt(value => value + 1)}>Retry accounting details</Button>
    </div>;
  }
  return <p role="status" className="text-sm text-muted-foreground">Loading accounting details…</p>;
}
